import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { forwardRef, useImperativeHandle, useRef } from "react";

const SITE_KEY =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ||
  "0x4AAAAAADuFJFvmUu9I9Fnf";

export interface TurnstileWidgetHandle {
  reset: () => void;
}

interface Props {
  onVerify: (token: string) => void;
  onExpire?: () => void;
  onError?: () => void;
  className?: string;
}

/**
 * Cloudflare Turnstile widget. If VITE_TURNSTILE_SITE_KEY is not configured,
 * renders nothing and immediately calls onVerify("") so dev/preview keeps
 * working (paired with the server-side bypass in verifyTurnstile.ts).
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, Props>(
  ({ onVerify, onExpire, onError, className }, ref) => {
    const instanceRef = useRef<TurnstileInstance | null>(null);

    useImperativeHandle(ref, () => ({
      reset: () => instanceRef.current?.reset(),
    }));

    // No site key configured — bypass in dev / preview.
    if (!SITE_KEY) {
      // Fire once so callers relying on a token can proceed.
      queueMicrotask(() => onVerify(""));
      return null;
    }

    return (
      <div className={className}>
        <Turnstile
          ref={instanceRef}
          siteKey={SITE_KEY}
          onSuccess={onVerify}
          onExpire={onExpire}
          onError={onError}
          options={{ theme: "auto", size: "flexible" }}
        />
      </div>
    );
  }
);

TurnstileWidget.displayName = "TurnstileWidget";
